import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Button,
  TextField,
  Autocomplete,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  CircularProgress,
  LinearProgress,
  Tooltip,
  Grid,
} from '@mui/material';
import {
  SearchOutlined,
  VisibilityOutlined,
  StopCircleOutlined,
  FiberManualRecord,
  EventOutlined,
  PeopleOutlined,
  AccessTimeOutlined,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import adminService from '../../services/admin.service';
import { useNotification } from '../../context/NotificationContext';
import PulsingDot from '../../components/common/PulsingDot';

// ---------- Types ----------

interface SessionWorkshop {
  id: string;
  title: string;
}

interface SessionInstructor {
  id: string;
  firstName: string;
  lastName: string;
}

interface SessionRow {
  id: string;
  name: string;
  status: string;
  scheduledStart: string;
  actualStart: string | null;
  actualEnd: string | null;
  duration: number | null;
  studentCount: number;
  workshop: SessionWorkshop;
  instructor: SessionInstructor;
  createdAt: string;
}

interface ActiveSession {
  id: string;
  name: string;
  status: string;
  workshopTitle: string;
  instructorName: string;
  studentCount: number;
  elapsedMinutes: number;
  avgProgress: number;
  scheduledStart: string;
  actualStart: string | null;
}

interface InstructorOption {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface WorkshopOption {
  id: string;
  title: string;
}

// ---------- Constants ----------

const statusColorMap: Record<string, 'info' | 'success' | 'warning' | 'default' | 'error'> = {
  scheduled: 'info',
  preparing: 'warning',
  ready: 'success',
  active: 'success',
  paused: 'warning',
  completed: 'default',
  cancelled: 'error',
};

const statusOptions = ['all', 'scheduled', 'active', 'paused', 'completed', 'cancelled'] as const;

// ---------- Component ----------

const AdminSessions: React.FC = () => {
  const { showNotification } = useNotification();
  const navigate = useNavigate();

  // Sessions table state
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [instructorFilter, setInstructorFilter] = useState<InstructorOption | null>(null);
  const [workshopFilter, setWorkshopFilter] = useState<WorkshopOption | null>(null);
  const [sortBy, setSortBy] = useState('scheduledStart');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [loading, setLoading] = useState(true);

  // Active sessions
  const [activeSessions, setActiveSessions] = useState<ActiveSession[]>([]);

  // Filter dropdown data
  const [instructors, setInstructors] = useState<InstructorOption[]>([]);
  const [workshops, setWorkshops] = useState<WorkshopOption[]>([]);

  // Force end dialog
  const [forceEndOpen, setForceEndOpen] = useState(false);
  const [forceEndSession, setForceEndSession] = useState<{ id: string; name: string; studentCount: number } | null>(null);
  const [forceEndReason, setForceEndReason] = useState('');
  const [forceEndLoading, setForceEndLoading] = useState(false);

  // Debounced search
  const [searchInput, setSearchInput] = useState('');

  // Refs for cleanup
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ---------- Fetchers ----------

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminService.getSessions({
        page: page + 1,
        limit: rowsPerPage,
        search: search || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        instructorId: instructorFilter?.id || undefined,
        workshopId: workshopFilter?.id || undefined,
        sortBy,
        sortOrder,
      });
      setSessions(res.data.sessions);
      setTotal(res.data.total);
    } catch {
      showNotification('Failed to load sessions', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, statusFilter, instructorFilter, workshopFilter, sortBy, sortOrder]);

  const fetchActiveSessions = useCallback(async () => {
    try {
      const res = await adminService.getActiveSessions();
      setActiveSessions(res.data);
    } catch {
      showNotification('Failed to load active sessions', 'error');
    }
  }, []);

  const fetchFilterData = useCallback(async () => {
    try {
      const [instrRes, wsRes] = await Promise.all([
        adminService.getInstructors(),
        adminService.getSessionWorkshops(),
      ]);
      setInstructors(instrRes.data);
      setWorkshops(wsRes.data);
    } catch {
      showNotification('Failed to load filter data', 'error');
    }
  }, []);

  // ---------- Effects ----------

  // Initial load of filter dropdown data
  useEffect(() => {
    fetchFilterData();
  }, [fetchFilterData]);

  // Sessions list fetch
  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  // Active sessions fetch + auto-refresh every 30s
  useEffect(() => {
    fetchActiveSessions();
    intervalRef.current = setInterval(fetchActiveSessions, 30000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchActiveSessions]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // ---------- Handlers ----------

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const openForceEnd = (session: { id: string; name: string; studentCount: number }) => {
    setForceEndSession(session);
    setForceEndReason('');
    setForceEndOpen(true);
  };

  const handleForceEnd = async () => {
    if (!forceEndSession || !forceEndReason.trim()) return;
    setForceEndLoading(true);
    try {
      await adminService.forceEndSession(forceEndSession.id, forceEndReason.trim());
      setForceEndOpen(false);
      fetchSessions();
      fetchActiveSessions();
    } catch {
      showNotification('Failed to force-end session', 'error');
    } finally {
      setForceEndLoading(false);
    }
  };

  // ---------- Computed ----------

  const scheduledCount = sessions.filter((s) => s.status === 'scheduled').length;
  const completedCount = sessions.filter((s) => s.status === 'completed').length;

  // ---------- Render ----------

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Session Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <Chip
            label={`Total: ${total}`}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 500 }}
          />
          <Chip
            icon={<PulsingDot size={7} />}
            label={`Active: ${activeSessions.length}`}
            size="small"
            color="success"
            variant="outlined"
            sx={{ fontWeight: 500, '& .MuiChip-icon': { ml: '6px' } }}
          />
          <Chip
            label={`Scheduled: ${scheduledCount}`}
            size="small"
            color="info"
            variant="outlined"
            sx={{ fontWeight: 500 }}
          />
          <Chip
            label={`Completed: ${completedCount}`}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 500 }}
          />
        </Box>
      </Box>

      {/* Active Sessions Panel */}
      {activeSessions.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            mb: 2.5,
            borderRadius: '12px',
            borderLeft: '4px solid #22c55e',
            bgcolor: 'rgba(34, 197, 94, 0.04)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <PulsingDot size={10} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Active Sessions
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {activeSessions.map((s) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={s.id}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: '10px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                  }}
                >
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                      {s.workshopTitle}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {s.instructorName}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PeopleOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {s.studentCount}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <AccessTimeOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {s.elapsedMinutes} min
                      </Typography>
                    </Box>
                  </Box>

                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Avg progress
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {Math.round(s.avgProgress)}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={s.avgProgress}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        bgcolor: 'rgba(0,0,0,0.06)',
                        '& .MuiLinearProgress-bar': { borderRadius: 3 },
                      }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<VisibilityOutlined sx={{ fontSize: 16 }} />}
                      onClick={() => navigate(`/admin/sessions/${s.id}`)}
                      sx={{ flex: 1, textTransform: 'none', fontSize: '0.8rem' }}
                    >
                      View
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<StopCircleOutlined sx={{ fontSize: 16 }} />}
                      onClick={() => openForceEnd({ id: s.id, name: s.name, studentCount: s.studentCount })}
                      sx={{ flex: 1, textTransform: 'none', fontSize: '0.8rem' }}
                    >
                      Force End
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2.5, borderRadius: '12px' }} elevation={0}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search sessions..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{ minWidth: 260 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined sx={{ color: 'text.secondary', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {statusOptions.map((s) => (
              <Chip
                key={s}
                label={s.charAt(0).toUpperCase() + s.slice(1)}
                size="small"
                variant={statusFilter === s ? 'filled' : 'outlined'}
                color={s === 'all' ? 'default' : statusColorMap[s] || 'default'}
                onClick={() => { setStatusFilter(s); setPage(0); }}
                sx={{ fontWeight: statusFilter === s ? 600 : 400 }}
              />
            ))}
          </Box>
          <Autocomplete
            size="small"
            options={instructors}
            getOptionLabel={(opt) => `${opt.firstName} ${opt.lastName}`}
            value={instructorFilter}
            onChange={(_, val) => { setInstructorFilter(val); setPage(0); }}
            renderInput={(params) => <TextField {...params} placeholder="Instructor" />}
            sx={{ minWidth: 200 }}
          />
          <Autocomplete
            size="small"
            options={workshops}
            getOptionLabel={(opt) => opt.title}
            value={workshopFilter}
            onChange={(_, val) => { setWorkshopFilter(val); setPage(0); }}
            renderInput={(params) => <TextField {...params} placeholder="Tech Eval" />}
            sx={{ minWidth: 200 }}
          />
        </Box>
      </Paper>

      {/* Sessions Table */}
      <Paper sx={{ borderRadius: '12px', overflow: 'hidden' }} elevation={0}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#fafafa' }}>
                <TableCell sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={sortBy === 'name'}
                    direction={sortBy === 'name' ? sortOrder : 'asc'}
                    onClick={() => handleSort('name')}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={sortBy === 'workshop'}
                    direction={sortBy === 'workshop' ? sortOrder : 'asc'}
                    onClick={() => handleSort('workshop')}
                  >
                    Evaluation
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={sortBy === 'instructor'}
                    direction={sortBy === 'instructor' ? sortOrder : 'asc'}
                    onClick={() => handleSort('instructor')}
                  >
                    Instructor
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={sortBy === 'status'}
                    direction={sortBy === 'status' ? sortOrder : 'asc'}
                    onClick={() => handleSort('status')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={sortBy === 'scheduledStart'}
                    direction={sortBy === 'scheduledStart' ? sortOrder : 'asc'}
                    onClick={() => handleSort('scheduledStart')}
                  >
                    Scheduled
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Students</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : sessions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    <EventOutlined sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography color="text.secondary">No sessions found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sessions.map((s) => (
                  <TableRow
                    key={s.id}
                    hover
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f8f9fa' } }}
                    onClick={() => navigate(`/admin/sessions/${s.id}`)}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {s.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {s.workshop.title}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {s.instructor.firstName} {s.instructor.lastName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={s.status.charAt(0).toUpperCase() + s.status.slice(1)}
                        size="small"
                        color={statusColorMap[s.status] || 'default'}
                        icon={
                          s.status === 'active' ? (
                            <FiberManualRecord sx={{ fontSize: '10px !important' }} />
                          ) : undefined
                        }
                        sx={{ fontWeight: 500, textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={dayjs(s.scheduledStart).format('MMM D, YYYY h:mm A')}>
                        <Typography variant="body2" color="text.secondary">
                          {dayjs(s.scheduledStart).fromNow()}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {s.studentCount}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {s.duration != null ? `${s.duration} min` : '\u2014'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="View">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/admin/sessions/${s.id}`)}
                        >
                          <VisibilityOutlined sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      {(s.status === 'active' || s.status === 'paused') && (
                        <Tooltip title="Force End">
                          <IconButton
                            size="small"
                            sx={{ color: 'error.main' }}
                            onClick={() =>
                              openForceEnd({ id: s.id, name: s.name, studentCount: s.studentCount })
                            }
                          >
                            <StopCircleOutlined sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 20, 50]}
        />
      </Paper>

      {/* Force End Dialog */}
      <Dialog
        open={forceEndOpen}
        onClose={() => setForceEndOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px' } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Force End Session</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="error.main" sx={{ mb: 2 }}>
            This will immediately end the session for all {forceEndSession?.studentCount ?? 0} students.
          </Typography>
          <TextField
            label="Reason"
            placeholder="Explain why this session is being force-ended..."
            fullWidth
            multiline
            rows={3}
            value={forceEndReason}
            onChange={(e) => setForceEndReason(e.target.value)}
            required
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setForceEndOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleForceEnd}
            disabled={forceEndLoading || !forceEndReason.trim()}
          >
            {forceEndLoading ? <CircularProgress size={20} /> : 'Force End Session'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminSessions;
